import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { TOUR_STEPS, type TourStep } from '../data/tourSteps';

interface TourContextType {
  isOpen: boolean;
  currentStepIndex: number;
  totalSteps: number;
  currentStep: TourStep | null;
  startTour: () => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = isOpen && currentStepIndex < TOUR_STEPS.length 
    ? TOUR_STEPS[currentStepIndex] 
    : null;

  const startTour = useCallback(() => {
    setCurrentStepIndex(0);
    setIsOpen(true);
    
    // Navigate to first step's route
    const firstStep = TOUR_STEPS[0];
    if (firstStep) {
      navigateToStep(firstStep);
    }
  }, []);

  const endTour = useCallback(() => {
    setIsOpen(false);
    setCurrentStepIndex(0);
    localStorage.removeItem('tour-active-tab');
  }, []);

  // Navigation helper
  const navigateToStep = (step: TourStep) => {
    const currentPath = window.location.pathname;
    const routeChanged = step.route !== currentPath;
    
    // Check if route is different
    if (routeChanged) {
      // Navigate to new route
      window.history.pushState({}, '', step.route);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    
    // Handle tab switching via query param
    if (step.tab) {
      const switchTab = () => {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', step.tab);
        window.history.replaceState({}, '', url.toString());
        localStorage.setItem('tour-active-tab', step.tab);
        
        // Trigger a custom event for tab changes
        window.dispatchEvent(new CustomEvent('tour-tab-change', { detail: { tab: step.tab } }));
      };
      
      // If route changed, wait for component to mount before switching tab
      if (routeChanged) {
        setTimeout(switchTab, 300);
      } else {
        switchTab();
      }
    } else {
      // Clear tab if not specified
      const url = new URL(window.location.href);
      if (url.searchParams.has('tab')) {
        url.searchParams.delete('tab');
        window.history.replaceState({}, '', url.toString());
      }
      localStorage.removeItem('tour-active-tab');
    }
  };

  const nextStep = useCallback(() => {
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      const nextIdx = currentStepIndex + 1;
      setCurrentStepIndex(nextIdx);
      navigateToStep(TOUR_STEPS[nextIdx]);
    } else {
      endTour();
    }
  }, [currentStepIndex, endTour]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevIdx = currentStepIndex - 1;
      setCurrentStepIndex(prevIdx);
      navigateToStep(TOUR_STEPS[prevIdx]);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step < TOUR_STEPS.length) {
      setCurrentStepIndex(step);
      navigateToStep(TOUR_STEPS[step]);
    }
  }, []);

  // Navigation effect: Check if current step matches location
  useEffect(() => {
    if (!isOpen || !currentStep) return;
    
    const currentPath = window.location.pathname;
    
    // If route doesn't match, navigate to correct route
    if (currentStep.route !== currentPath) {
      navigateToStep(currentStep);
    }
  }, [isOpen, currentStep]);

  return (
    <TourContext.Provider
      value={{
        isOpen,
        currentStepIndex,
        totalSteps: TOUR_STEPS.length,
        currentStep,
        startTour,
        endTour,
        nextStep,
        prevStep,
        goToStep,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
